import React, { createContext, useState, useCallback, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../../../shared/services/users/usersServices';
import { userAttributesService } from '../../../shared/services/userAtributes/userAtributes';
import { format } from 'date-fns';
import type { Resident } from '../types/resident.types';

export interface ResidentContextData {
  // Listagem
  residents: Resident[];
  isLoading: boolean;
  loading: boolean;
  error: string | null;
  handleCreate: () => void;
  handleView: (resident: Resident) => void;
  handleCloseError: () => void;
  
  // Modal de Delete
  isDeleteModalOpen: boolean;
  openDeleteModal: (resident: Resident) => void;
  closeDeleteModal: () => void;
  handleDeleteResident: () => void;
  isDeleting: boolean;
  
  // Form
  residentData: Resident | null;
  isDataLoading: boolean;
  createResident: (data: any) => Promise<void>;
  updateResident: (id: string, data: any) => Promise<void>;
  isSubmitting: boolean;
  setError: (error: string | null) => void;
  navigateToEdit: (id: string) => void;
}

interface ResidentProviderProps {
  children: ReactNode;
}

export const ResidentContext = createContext<ResidentContextData>({} as ResidentContextData);

export function ResidentProvider({ children }: ResidentProviderProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedResident, setSelectedResident] = useState<Resident | null>(null);

  // Query para listar residentes (filtrar por type_flag = CONDOMINO)
  const { data: residents = [], isLoading } = useQuery({
    queryKey: ['residents'],
    queryFn: async () => {
      try {
        const response = await userService.list({ role: 'customer' });
        return response.data || [];
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar residentes';
        setError(errorMessage);
        return [];
      }
    }
  });

  // Query para buscar dados do residente no modo de edição
  const { data: residentData = null, isLoading: isDataLoading } = useQuery({
    queryKey: ['resident', id],
    queryFn: async () => {
      if (!id) return null;
      try {
        const response = await userService.get(id);
        console.log('Dados carregados para edição:', response.data);
        return response.data;
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar residente';
        setError(errorMessage);
        return null;
      }
    },
    enabled: !!id,
  });


  // Mutation para criar residente
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      console.log('CreateMutation - data recebida:', data);
      
      const formData = new FormData();
      
      // Campos obrigatórios
      formData.append('name', data.name || '');
      formData.append('email', data.email || '');
      formData.append('status', data.status);
      formData.append('role', data.role);
      formData.append('documentNumber', data.documentNumber);
      formData.append('phoneNumber', data.phoneNumber);
      formData.append('password', 'morador@123');
      
      // Campos opcionais
      formData.append('description', data.description || 'Residente');
      
      if (data.tenants) {
        data.tenants.forEach((tenant: any, index: any) => {
          formData.append(`tenants[${index}][id]`, tenant.id);
          formData.append(`tenants[${index}][role]`, data.role);
        });
      }
      
   
      if (data.avatarFile) {
        formData.append('file', data.avatarFile);
      }

      console.log('CreateMutation - FormData preparada:');
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }

      const userResponse = await userService.create(formData);
      console.log('CreateMutation - userResponse:', userResponse);
      
      const userId = userResponse?.data?.id;

      if (!userId) {
        throw new Error('ID do usuário não retornado');
      }

      // Criar atributos do residente
      const attributesPayload = {
        userId,
        attributes: [
          { key: "status_flag", value: data.status_flag || "ACTIVE" },
          { key: "document", value: data.document || "" },
          { key: "nationality", value: data.nationality || "" },
          { key: "birth_date", value: data.birth_date ? format(new Date(data.birth_date), 'yyyy-MM-dd') : "" },
          { key: "gender_flag", value: data.gender_flag || null },
          { key: "marital_flag", value: data.marital_flag || null },
          { key: "type_flag", value: "CONDOMINO" },
          { key: "phone_number", value: data.phone_number || "" },
          {
            key: "cnh",
            value: data.cnh_number && data.cnh_expiration
              ? `${data.cnh_number} - ${format(new Date(data.cnh_expiration), 'yyyy-MM-dd')}`
              : ""
          },
          ...(data.unit ? [{ key: "unit", value: data.unit }] : []),
          ...(data.section_name ? [{ key: "section_name", value: data.section_name }] : []),
        ]
      };

      console.log('CreateMutation - attributesPayload:', attributesPayload);
      
      await userAttributesService.create(attributesPayload);
      console.log('CreateMutation - Atributos criados com sucesso');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['residents'] });
    },
    onError: (error: any) => {
      console.error('CreateMutation - Erro:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar residente';
      setError(errorMessage);
      throw error;
    }
  });

  // Mutation para atualizar residente
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const userData: any = {
        name: data.name,
        email: data.email,
        description: data.description || 'Residente',
      };
      
      if (data.password) userData.password = data.password;
      
      await userService.update(id, userData);

      // Atualizar atributos do residente
      const attributesPayload = {
        userId: id,
        attributes: [
          { key: "status_flag", value: data.status_flag || "ACTIVE" },
          { key: "document", value: data.document || "" },
          { key: "nationality", value: data.nationality || "" },
          { key: "birth_date", value: data.birth_date ? format(new Date(data.birth_date), 'yyyy-MM-dd') : "" },
          { key: "gender_flag", value: data.gender_flag || null },
          { key: "marital_flag", value: data.marital_flag || null },
          { key: "type_flag", value: "CONDOMINO" }, // Sempre CONDOMINO para residentes
          { key: "phone_number", value: data.phone_number || "" },
          {
            key: "cnh",
            value: data.cnh_number && data.cnh_expiration
              ? `${data.cnh_number} - ${format(new Date(data.cnh_expiration), 'yyyy-MM-dd')}`
              : ""
          },
          ...(data.unit ? [{ key: "unit", value: data.unit }] : []),
          ...(data.section_name ? [{ key: "section_name", value: data.section_name }] : []),
        ]
      };

      await userAttributesService.update(attributesPayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['residents'] });
      queryClient.invalidateQueries({ queryKey: ['resident', id] });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao atualizar residente';
      setError(errorMessage);
      throw error;
    }
  });

  // Mutation para deletar residente
  const deleteMutation = useMutation({
    mutationFn: (id: string) => userService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['residents'] });
      closeDeleteModal();
      navigate('/residents');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao deletar residente';
      setError(errorMessage);
    }
  });

  const handleCreate = useCallback(() => {
    navigate('/residents/new');
  }, [navigate]);

  const handleView = useCallback((resident: Resident) => {
    navigate(`/residents/${resident.id}`);
  }, [navigate]);

  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  const openDeleteModal = useCallback((resident: Resident) => {
    setSelectedResident(resident);
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setSelectedResident(null);
  }, []);

  const handleDeleteResident = useCallback(() => {
    if (selectedResident) {
      deleteMutation.mutate(selectedResident.id);
    }
  }, [selectedResident, deleteMutation]);

  const createResident = useCallback(async (data: any) => {
    await createMutation.mutateAsync(data);
  }, [createMutation]);

  const updateResident = useCallback(async (id: string, data: any) => {
    await updateMutation.mutateAsync({ id, data });
  }, [updateMutation]);

  const navigateToEdit = useCallback((id: string) => {
    navigate(`/residents/${id}/edit`);
  }, [navigate]);

  return (
    <ResidentContext.Provider value={{
      residents,
      isLoading,
      loading: createMutation.isPending || updateMutation.isPending,
      error,
      handleCreate,
      handleView,
      handleCloseError,
      isDeleteModalOpen,
      openDeleteModal,
      closeDeleteModal,
      handleDeleteResident,
      isDeleting: deleteMutation.isPending,
      residentData,
      isDataLoading,
      createResident,
      updateResident,
      isSubmitting: createMutation.isPending || updateMutation.isPending,
      setError,
      navigateToEdit,
    }}>
      {children}
    </ResidentContext.Provider>
  );
}